import React, { useState, useEffect, useRef } from "react";
import Typed from "typed.js";
import Header from "../Components/Header";
import Sidebar from "../Components/Sidebar";
import Footer from "../Components/Footer";
import RouteApi from "../Backend/Backend";
import { useSelector } from "react-redux";
import { useParams, useNavigate, Link } from "react-router-dom";
import confetti from "canvas-confetti";
import Swal from "sweetalert2";
import CBTCarousel from "./CBTCarousel";
import { isMobile } from "react-device-detect";
import { toast } from "react-toastify";
import CbtList from "./CBTList";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

const CBT = () => {
  const navigate = useNavigate();
  const { id } = useParams(); // Retrieve the dynamic id from the route params
  const { handleCbtSubmit, getSubjects, getCbtList } = RouteApi();

  const modules = {
    toolbar: [
      [{ header: [1, 2, 3, false] }],
      ["bold", "italic", "underline", "strike"],
      [{ align: [] }],
      [{ list: "ordered" }, { list: "bullet" }],
      [{ color: [] }, { background: [] }],
      [{ script: "sub" }, { script: "super" }], // Added line for sub/super
      ["blockquote", "code-block"],
      ["link", "image", "video"],
      ["clean"],
    ],
  };

  const formats = [
    "header", "bold", "italic", "underline", "strike",
    "align", "list", "bullet",
    "color", "background",
    "script", // Added "script" format
    "blockquote", "code-block",
    "link", "image", "video"
  ];

  const [trigger, setTrigger] = useState(null);
  const [message, setMessage] = useState("");
  const [required, setRequired] = useState("");

  const typedRef = useRef(null);

  useEffect(() => {
    const typed = new Typed(typedRef.current, {
      strings: [
        "Need help creating questions?",
        "Want to generate tasks quickly?",
        "Looking for AI assistance?",
      ],
      typeSpeed: 50,
      backSpeed: 30,
      loop: true,
    });

    return () => {
      typed.destroy();
    };
  }, []);

  const typedElement = useRef(null);
  const typedInstance = useRef(null);

  useEffect(() => {
    if (required) {
      typedInstance.current = new Typed(typedElement.current, {
        strings: [
          
          trigger === "Description"
            ? required
            : trigger === "cbt_name_exists"
            ? required + "- CBT name already exists. Please choose a different name."
            : trigger === "Ai_Response"
            ? required + "- CBT name already exists. Please choose a different name."
            : trigger
          
        ],
        typeSpeed: 40,
        backSpeed: 25,
        backDelay: 3000,
        startDelay: 500,
        loop: false,
        smartBackspace: true,
        showCursor: true,
        cursorChar: "|"
      });
    }

    return () => {
      typedInstance.current?.destroy();
      typedInstance.current = null;
    };
  }, [required]);

  const user = useSelector((state) => state.auth?.user);

  // Get user details
  const fname = user?.ghostuser || ""; // Default to empty if not available
  const email = user?.email || "";
  const phone = user?.phone || "";

  useEffect(() => {
    confetti({
      particleCount: 500,
      spread: 100,
      origin: { y: 0.6 },
      colors: ["#ffffff", "#d291bc", "#957dad"],
    });
  }, []);

  const [cbtList, setCbtList] = useState([]);

  const loadCbtList = async () => {
    const cbtData = await getCbtList();
    console.log("CBT Data:", cbtData);

    const { success, tasks } = cbtData;

    if (success && Array.isArray(tasks)) {
      setCbtList(tasks);
    } else {
      setCbtList([]);
    }
  };

  useEffect(() => {
    loadCbtList(); // initial load
  }, []);

  const [cbtData, setCbtData] = useState({
    name: "",
    type: "",
    subject: "",
    studentClass: "",
    duration: "",
    accessPassword: "",
    description: "Set up Instruction, [number] Objective questions, [number] Theory questions, for [subject] [class] [time number] minutes. Nigeria Scheme",
    lenient: "Medium",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCbtData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const [isSubmitting, setIsSubmitting] = useState(false);

  const submitCbt = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const response = await handleCbtSubmit(cbtData);
      if (response.success) {
        toast.success("CBT created successfully!", { /* toast config */ });

        setTimeout(() => {
          navigate("./create", {
            state: {
              trigger: response.trigger,
              required: response.required,
              message: response.message,
              cbtId: response.token,
            },
          });
        }, 2000);

        const refreshed = await getCbtList();
        if (refreshed.success && Array.isArray(refreshed.cbts)) {
          setCbtList(refreshed.cbts);
        }
        
      } else {
        setTrigger(response.trigger || null);
        setMessage(response.message || "An error occurred.");
        setRequired(response.required || "Please fill all required fields.");
      }
    } catch (err) {
      Swal.fire({
        text: "An error occurred while creating CBT.",
        icon: "error",
        confirmButtonText: "Ok, got it!",
        customClass: { confirmButton: "btn fw-bold btn-danger" },
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const [subjects, setSubjects] = useState([]);

  useEffect(() => {
    // dynamically set this as needed
    getSubjects(id, setSubjects);
    //setTrigger('Description');
    localStorage.setItem("lastSelectedCbtColumnId", 1);
  }, []);

  return (
    <div className="bg-back">
      <Header />
      <Sidebar />

      <div className="docs-wrapper bg-back">
        <div className="position-relative overflow-hidden px-0 pt-10 pt-lg-14 pb-8 pb-lg-10"
         style={{
          backgroundImage: "url('../../assets/images/background/12.svg')",
          backgroundRepeat: "no-repeat",
          backgroundSize: "cover", // full visible image
          backgroundPosition: "100% 100%",
          minHeight: "400px", // adjust height for visibility
        }}>
          {/* 🎉 Gradient Waves */}
          <div className="gradient-waves position-absolute top-0 start-0 w-100 h-100 z-n1"></div>

          {/* ✨ Floating Icons */}
          <div className="floaty-icon icon1">
            <i className="bi bi-mortarboard-fill fs-1 text-gray-100"></i>
          </div>
          <div className="floaty-icon icon2 fs-t text-gray-100">
            <i className="bi bi-123 fs-1 text-gray-300"></i>
          </div>
          <div className="floaty-icon icon3">
            <i className="bi bi-balloon-fill fs-1 text-gray-100"></i>
          </div>

          <div className="container-fluid px-6 px-lg-14">
            <div className="row align-items-center mt-4 mt-lg-0 mb-4 mb-lg-0">
              <div className="col-12 col-lg-7">
                <div className="text-purple2 fs-t2 fw-medium">
                  Welcome To CBT Pro Version{" "}
                  <i className="bi bi-stars text-warning fs-3"></i>
                </div>
                <p className="text-purple2 fs-4 mb-0">
                  Now you can create and manage your CBT tasks with ease. With
                  the new AI-powered task generation, you can create tasks in
                  seconds. Just provide a brief description, and let the AI
                  generate questions for you!
                </p>
              </div>

              <div className="col-12 text-lg-end mt-4 mt-lg-0">
                <div className="btn btn-light btn-md px-4 rounded-3"
                data-bs-toggle="offcanvas"
                data-bs-target="#open_cbt_console"
                aria-controls="offcanvasRight">
                  <span className="d-lg-inline">
                    Visit CBT Control Console
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="container-fluid px-2 px-lg-12 pt-lg-12 pt-8 pb-10 pb-lg-10 mt-lg-n18 mt-n12">
          <div
            className="card bg-white shadow-none px-lg-6 px-4 py-4"
            style={{ borderRadius: "18px" }}
          >
            <div className="row gx-6 gy-4 align-items-center">
              <div className="d-flex align-items-center">
                <div className="col">
                  <div className="fs-10 text-q2 fw-medium">
                    <span className="">Workspace</span>
                  </div>
                </div>

                <div className="col-end d-none d-lg-block">
                <div className="text-gray-500 fs-4 mt-3">
                      <i className="bi bi-stars text-warning fs-3"></i> Smart Ai
                      <span className="text-dark fw-semibold ms-2">
                        Hey there! <span ref={typedRef}></span>
                      </span>
                    </div>
                </div>
              </div>

              <div className="col-12 px-lg-8 px-3">
                <div className="">
                  <div className="text-dark fs-3 fw-medium mb-3">
                    <i className="bi bi-plus-lg "></i> Create CBT
                  </div>
                </div>

                <div className="row gx-6 gy-4 align-items-center justify-content-between">
                  <form className="col-12 col-lg-12" onSubmit={submitCbt}>
                    <div className="row gx-6 gy-2 mb-9 mb-lg-2">
                      <div className="col-12 col-lg-6 mb-2">
                        <div className="form-group">
                          <input
                            type="text"
                            name="name"
                            id="name-input"
                            value={cbtData.name}
                            onChange={handleChange}
                            placeholder="CBT Name ID"
                            required
                            className="form-control form-control-lg rounded-4 bg-back text-dark"
                          />
                        </div>
                      </div>

                      <div className="col-12 col-lg-6 mb-2">
                        <div className="form-group">
                          <select
                            name="type"
                            value={cbtData.type}
                            onChange={handleChange}
                            required
                            className="form-select form-select-lg rounded-4 bg-back text-dark"
                          >
                            <option value="">Select CBT Type</option>
                            <option value="test">Test</option>
                            <option value="exam">Exam</option>
                          </select>
                        </div>
                      </div>

                      <div className="col-12 col-lg-6 mb-2">
                        <div className="form-group">
                          <select
                            name="subject"
                            value={cbtData.subject}
                            onChange={handleChange}
                            required
                            className="form-select form-select-lg rounded-4 bg-back text-dark"
                          >
                            <option value="">Select Subject</option>
                            {subjects.map((subject, index) => (
                              <option key={index} value={subject}>
                                {subject}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div className="col-12 col-lg-6 mb-2">
                        <div className="form-group">
                          <select
                            name="studentClass"
                            value={cbtData.studentClass}
                            onChange={handleChange}
                            className="form-select form-select-lg rounded-4 bg-back text-dark"
                            required
                          >
                            <option value="">Select Class</option>
                            {[
                              "Preparatory 1",
                              "Preparatory 2",
                              "Reception 1",
                              "Reception 2",
                              "PlayGroup",
                              "NUR 1",
                              "NUR 2",
                              "KG 1",
                              "KG 2",
                              "Grade 1",
                              "Grade 2",
                              "Grade 3",
                              "Grade 4",
                              "Grade 5",
                              "Grade 6",
                              "Jss1",
                              "Jss2",
                              "Jss3",
                              "sss1",
                              "sss2",
                              "sss3",
                            ].map((cls) => (
                              <option key={cls} value={cls}>
                                {cls}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div className="col-12 col-lg-6 mb-2">
                        <div className="form-group">
                          <input
                            type="number"
                            name="duration"
                            value={cbtData.duration}
                            onChange={handleChange}
                            className="form-control form-control-lg rounded-4 bg-back text-dark"
                            placeholder="Duration (minutes)"
                            min="1"
                            required
                          />
                        </div>
                      </div>

                      <div className="col-12 col-lg-6 mb-2">
                        <div className="form-group">
                          <input
                            type="text"
                            name="accessPassword"
                            value={cbtData.accessPassword}
                            onChange={handleChange}
                            className="form-control form-control-lg rounded-4 bg-back text-dark"
                            placeholder="Access Password"
                            required
                          />
                        </div>
                      </div>

                      <div className="col-12 col-lg-8 mb-2">
                        <div className="form-group">
                          <select
                            name="lenient"
                            value={cbtData.lenient}
                            onChange={handleChange}
                            required
                            className="form-select form-select-lg rounded-4 bg-back text-dark"
                          >
                            <option value="">Select Mark Leniency</option>
                            <option value="Easy">Easy</option>
                            <option value="Medium">Medium</option>
                            <option value="Hard">Hard</option>
                          </select>
                        </div>
                      </div>

                      <div className="col-12 mb-5">
                        <div className="form-group">
                          <ReactQuill
                            theme="snow"
                            value={cbtData.description}
                            onChange={(value) =>
                              setCbtData((prev) => ({ ...prev, description: value }))
                            }
                            modules={modules}
                            formats={formats}
                            placeholder="Optional description for AI to generate questions"
                            className="form-control form-control-lg rounded-4 bg-back text-dark quill-no-border"
                            style={{ height: "200px" }} // 👈 increase height here
                          />
                        </div>
                      </div>

                    </div>

                    <div>
                      <button
                        className="btn btn-dark btn-lg rounded-4 px-5 mt-4 d-flex align-items-center justify-content-center gap-2"
                        type="submit"
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? (
                          <>
                            <span className="spinner-border spinner-border-sm text-warning" role="status" aria-hidden="true"></span>
                            <span>Generating...</span>
                          </>
                        ) : (
                          <>
                            <i className="bi bi-stars text-warning"></i> Create CBT
                          </>
                        )}
                      </button>
                    </div>

                  </form>

                  <div className="col-12 col-lg-4 d-block d-lg-none text-center mt-lg-n12">
                    <div className="text-gray-500 fs-4 mt-3">
                      <i className="bi bi-stars text-warning fs-3"></i> Smart Ai
                      <span className="text-dark fw-semibold ms-2">
                        Hey there! <span ref={typedRef}></span>
                      </span>
                    </div>
                    <img
                      src="../assets/images/background/build.jpg"
                      alt="CBT Illustration"
                      style={{ maxWidth: "100%" }}
                    />
                  </div>

                  <div id="cbt-list">
                    <CBTCarousel cbtList={cbtList} loadCbtList={loadCbtList} />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {(trigger === "Description" || trigger === "cbt_name_exists" || trigger === "Ai_Response") && (
            <div
              className={`offcanvas offcanvas-bottom show popup-fade-in bg-purple shadow`}
              data-bs-scroll="true"
              data-bs-backdrop="false"
              tabIndex="-1"
              id="run_task"
              aria-labelledby="offcanvasBottomLabel"
              style={
                isMobile
                  ? {}
                  : {
                      borderRadius: "20px",
                      width: "350px",
                      height: "300px",
                      bottom: "90px",
                      left: "auto",
                      right: "5px",
                      border: "none",
                      boxShadow: "0 0 5px rgba(0, 0, 0, 0.1)",
                    }
              }
            >
              <div className="offcanvas-header">
                <div className="fs-3 text-white fw-medium" id="offcanvasBottomLabel">
                  <i className="bi bi-stars text-warning fs-t1"></i> Smart Ai Desk
                </div>
                <button
                  className="btn btn-white btn-icon rounded-br-lg2 rounded-bl-sm2 rounded-t-lg2"
                  data-bs-dismiss="offcanvas"
                  aria-label="Close"
                  onClick={() => setTrigger(null)}
                >
                  <i className="bi bi-x-lg fw-bold fs-3 text-dark"></i>
                </button>
              </div>
              <div className="offcanvas-body small py-0 px-lg-7">
                {trigger && (
                  <div className="mt-2">
                    <div className="fs-medium text-danger fs-3 mb-2">Error - {trigger}</div>
                    {required && (
                      <span className="text-white fs-4 mt-2 d-block">
                        <span ref={typedElement}></span>
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          <Footer />

          <div
          className="offcanvas offcanvas-end bg-white"
          tabIndex="-1"
          id="open_cbt_console"
          aria-labelledby="offcanvasRightLabel"
          role="dialog"
          style={
              isMobile
                  ? {}
                  : {
                      width: "400px",
                  }
              }
          >
          <div className="offcanvas-header align-items-center shadow-none py-3 px-4">
          <div className="text-dark m-0 fs-3" id="offcanvasRightLabel">
              <i className="bi bi-tv-fill text-dark"></i> {" "}
              Open CBT Console
          </div>
          <button
              type="button"
              className="btn btn-dark btn-icon rounded-br-lg2 rounded-bl-sm2 rounded-t-lg2"
              data-bs-dismiss="offcanvas"
              aria-label="Close"
          >
              <i className="bi bi-x-lg fw-bold fs-4 text-white"></i>
          </button>
          </div>

          <div className="offcanvas-body px-3 py-0">
           <CbtList cbtList={cbtList} loadCbtList={loadCbtList} />
          </div>
        </div>
        </div>
      </div>
    </div>
  );
};

export default CBT;
